import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { useActionsPortefeuille, useRetenuesPrets } from '@/api/hooks';
import { useSession } from '@/auth/session';
import { BoutonExcel, Chargement, ErreurBloc, PageHeader } from '@/components/common';
import { montantDevise } from '@/components/rh';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { dateFr, dateHeureFr } from '@/lib/utils';

const LIBELLE_ETAT = { PREPARE: 'Préparé', TRANSMIS: 'Transmis à la paie', CONFIRME: 'Confirmé par la paie' } as const;

/** État mensuel des retenues sur paie des prêts sociaux (§ 5.8, extraction mensuelle) : préparé et transmis par la Division des investissements ou la paie, confirmé par la paie. */
export function RetenuesPrets() {
  const { mois: moisParam } = useParams();
  const navigate = useNavigate();
  const u = useSession()!.utilisateur;
  const inv = u.profils.includes('INVESTISSEMENTS');
  const paie = u.profils.includes('PAIE');
  const auj = new Date();
  const mois = moisParam ?? `${auj.getUTCFullYear()}-${String(auj.getUTCMonth() + 1).padStart(2, '0')}`;
  const { data, isLoading, error } = useRetenuesPrets(mois);
  const actions = useActionsPortefeuille();
  const [saisie, setSaisie] = useState<Record<string, { montantConfirme: string; ecartMotif: string }>>({});
  const executer = async (fn: () => Promise<unknown>, succes: string) => {
    try {
      await fn();
      toast.success(succes);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action impossible.');
    }
  };
  const confirmer = () =>
    executer(
      () => actions.confirmerRetenues.mutateAsync({ mois, lignes: (data?.lignes ?? []).map((l) => ({ id: l.id, montantConfirme: saisie[l.id]?.montantConfirme !== undefined && saisie[l.id]!.montantConfirme !== '' ? Number(saisie[l.id]!.montantConfirme) : l.montantPrepare, ecartMotif: saisie[l.id]?.ecartMotif || undefined })) }),
      'Retenues confirmées.',
    );
  const enConfirmation = paie && data?.etat?.statut === 'TRANSMIS';
  return (
    <div>
      <PageHeader
        titre="Retenues sur paie des prêts sociaux"
        sousTitre="Échéances du mois à retenir sur la paie (principal, intérêts, montant à déduire) : état préparé et transmis par la Division des investissements, retenues confirmées une à une par la paie ; une échéance n'est jamais soldée parce que sa date est passée."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Input type="month" value={mois} onChange={(e) => navigate(`/rh/prets/retenues/${e.target.value}`)} aria-label="Mois" />
            <BoutonExcel chemin={`/rh/retenues/${mois}/export.xlsx`} nom={`retenues-prets-${mois}.xlsx`} libelle="Export Excel" taille="default" />
            {(inv || paie) && (!data?.etat || data.etat.statut === 'PREPARE') && (
              <Button variant="outline" onClick={() => executer(() => actions.preparerRetenues.mutateAsync(mois), 'État préparé.')} disabled={actions.preparerRetenues.isPending}>
                {data?.etat ? 'Préparer à nouveau' : 'Préparer l’état'}
              </Button>
            )}
            {(inv || paie) && data?.etat?.statut === 'PREPARE' && (
              <Button onClick={() => executer(() => actions.transmettreRetenues.mutateAsync(mois), 'État transmis à la paie.')} disabled={actions.transmettreRetenues.isPending}>
                Transmettre à la paie
              </Button>
            )}
            {enConfirmation && (
              <Button onClick={confirmer} disabled={actions.confirmerRetenues.isPending}>
                Confirmer les retenues
              </Button>
            )}
          </div>
        }
      />
      {isLoading ? (
        <Chargement />
      ) : error ? (
        <ErreurBloc erreur={error} />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-2">
              État de {mois} {data?.etat ? <Badge variant={data.etat.statut === 'CONFIRME' ? 'default' : 'secondary'}>{LIBELLE_ETAT[data.etat.statut]}</Badge> : <Badge variant="outline">non préparé</Badge>}
            </CardTitle>
            <CardDescription>
              {data?.etat ? `Préparé le ${dateHeureFr(data.etat.prepareLe)}${data.etat.transmisLe ? ` · transmis le ${dateHeureFr(data.etat.transmisLe)}` : ''}${data.etat.confirmeLe ? ` · confirmé le ${dateHeureFr(data.etat.confirmeLe)}` : ''} · ${data.lignes.length} ligne(s) · à déduire ${montantDevise(data.total.montant, 'USD')}` : `${data?.apercu ?? 0} échéance(s) du mois en attente de préparation.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!data?.lignes.length ? (
              <p className="text-sm text-muted-foreground">Aucune ligne.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Prêt</TableHead>
                      <TableHead>Agent</TableHead>
                      <TableHead className="hidden md:table-cell">Banque</TableHead>
                      <TableHead className="text-right">Décaissé</TableHead>
                      <TableHead className="text-right">Solde dû</TableHead>
                      <TableHead>Échéance</TableHead>
                      <TableHead className="text-right">Principal</TableHead>
                      <TableHead className="text-right">Intérêts</TableHead>
                      <TableHead className="text-right">À déduire</TableHead>
                      <TableHead className="text-right">Retenue confirmée</TableHead>
                      <TableHead>Écart</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.lignes.map((l) => (
                      <TableRow key={l.id}>
                        <TableCell>
                          <Link to={l.demandeId ? `/rh/prets/${l.demandeId}?onglet=remboursement` : `/rh/portefeuille/${l.pretId}`} className="font-medium text-primary hover:underline">
                            {l.numero}
                          </Link>
                          <div className="text-xs text-muted-foreground">dernière échéance {dateFr(l.derniereEcheance)}</div>
                        </TableCell>
                        <TableCell>
                          {l.agent.nom}
                          <div className="text-xs text-muted-foreground">
                            {l.agent.matricule} · {l.structureCode}
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">{l.banque ?? '—'}</TableCell>
                        <TableCell className="num text-right">{montantDevise(l.montantDecaisse, l.devise)}</TableCell>
                        <TableCell className="num text-right">{montantDevise(l.capitalRestant, l.devise)}</TableCell>
                        <TableCell>{l.echeance ? `n° ${l.echeance.numero} · ${dateFr(l.echeance.date)}` : '—'}</TableCell>
                        <TableCell className="num text-right">{montantDevise(l.principal, l.devise)}</TableCell>
                        <TableCell className="num text-right">{montantDevise(l.interets, l.devise)}</TableCell>
                        <TableCell className="num text-right font-medium">{montantDevise(l.montantPrepare, l.devise)}</TableCell>
                        <TableCell className="num text-right">
                          {enConfirmation ? (
                            <Input type="number" min={0} step="0.01" className="ml-auto w-28 text-right" value={saisie[l.id]?.montantConfirme ?? String(l.montantPrepare)} onChange={(e) => setSaisie({ ...saisie, [l.id]: { montantConfirme: e.target.value, ecartMotif: saisie[l.id]?.ecartMotif ?? '' } })} />
                          ) : l.montantConfirme === null ? (
                            '—'
                          ) : (
                            montantDevise(l.montantConfirme, l.devise)
                          )}
                        </TableCell>
                        <TableCell>
                          {enConfirmation ? (
                            <Input placeholder="Motif de l'écart" value={saisie[l.id]?.ecartMotif ?? ''} onChange={(e) => setSaisie({ ...saisie, [l.id]: { montantConfirme: saisie[l.id]?.montantConfirme ?? String(l.montantPrepare), ecartMotif: e.target.value } })} />
                          ) : (
                            (l.ecartMotif ?? (l.montantConfirme !== null && l.montantConfirme !== l.montantPrepare ? 'écart' : '—'))
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
